import { useState, useEffect } from 'react';
import { Icon, Button } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { useProductsList, useProductVariantsList } from '@/hooks/useErpApi';

interface SelectedVariant {
  id: string;
  name: string;
  productName: string;
}

interface ProductVariantSelectorProps {
  onSelect: (variants: SelectedVariant[]) => void;
  onCancel: () => void;
}

export function ProductVariantSelector({ onSelect, onCancel }: ProductVariantSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(new Set());
  const [selectedVariantDetails, setSelectedVariantDetails] = useState<
    Map<string, SelectedVariant>
  >(new Map());

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch products with search query
  const { data: products, isLoading: productsLoading } = useProductsList(
    debouncedSearch ? { search: debouncedSearch } : undefined
  );

  // Fetch variants for selected product
  const { data: variants, isLoading: variantsLoading } = useProductVariantsList(
    selectedProductId || '',
    !!selectedProductId
  );

  const handleProductSelect = (productId: string) => {
    setSelectedProductId(productId);
    // Clear variant selection when changing products
    setSelectedVariants(new Set());
    setSelectedVariantDetails(new Map());
  };

  const handleVariantToggle = (variantId: string, variantName: string, productName: string) => {
    setSelectedVariants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variantId)) {
        newSet.delete(variantId);
        setSelectedVariantDetails((prevDetails) => {
          const newMap = new Map(prevDetails);
          newMap.delete(variantId);
          return newMap;
        });
      } else {
        newSet.add(variantId);
        setSelectedVariantDetails((prevDetails) => {
          const newMap = new Map(prevDetails);
          newMap.set(variantId, { id: variantId, name: variantName, productName });
          return newMap;
        });
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (!variants) return;

    const product = products?.find((p) => p.id === selectedProductId);
    if (!product) return;

    if (selectedVariants.size === variants.length) {
      // Deselect all
      setSelectedVariants(new Set());
      setSelectedVariantDetails(new Map());
    } else {
      // Select all
      const newVariants = new Set<string>();
      const newDetails = new Map<string, SelectedVariant>();
      variants.forEach((variant) => {
        newVariants.add(variant.id);
        newDetails.set(variant.id, {
          id: variant.id,
          name: `${variant.brand_name}`,
          productName: product.name,
        });
      });
      setSelectedVariants(newVariants);
      setSelectedVariantDetails(newDetails);
    }
  };

  const handleSubmit = () => {
    onSelect(Array.from(selectedVariantDetails.values()));
  };

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center gap-4">
        <div
          className={`flex items-center gap-2 ${selectedProductId ? 'text-success-600' : 'text-primary-600'}`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${selectedProductId ? 'bg-success-100' : 'bg-primary-100'}`}
          >
            {selectedProductId ? <Icon name="Check" size={16} /> : '1'}
          </div>
          <span className="font-medium">Search Product</span>
        </div>
        <div className="flex-1 h-0.5 bg-neutral-200" />
        <div
          className={`flex items-center gap-2 ${selectedProductId ? 'text-primary-600' : 'text-neutral-400'}`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${selectedProductId ? 'bg-primary-100' : 'bg-neutral-100'}`}
          >
            2
          </div>
          <span className="font-medium">Select Variants</span>
        </div>
      </div>

      {!selectedProductId ? (
        /* Step 1: Product Search */
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Products
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={18} className="text-neutral-400" />
              </div>
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type product name to search..."
                className="pl-10"
                autoFocus
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">Start typing to search products by name</p>
          </div>

          <div className="border border-neutral-200 rounded-lg max-h-96 overflow-y-auto">
            {productsLoading ? (
              <div className="text-center py-12">
                <Icon
                  name="LoaderCircle"
                  size={32}
                  className="animate-spin text-neutral-400 mx-auto mb-2"
                />
                <p className="text-sm text-neutral-600">Searching products...</p>
              </div>
            ) : products && products.length > 0 ? (
              <div className="divide-y divide-neutral-200">
                {products.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductSelect(product.id)}
                    className="w-full p-4 hover:bg-neutral-50 text-left transition-colors flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-neutral-900">{product.name}</p>
                      <p className="text-sm text-neutral-600">
                        {product.description || 'No description'}
                      </p>
                    </div>
                    <Icon name="ChevronRight" size={20} className="text-neutral-400" />
                  </button>
                ))}
              </div>
            ) : searchQuery ? (
              <div className="text-center py-12">
                <Icon name="Search" size={32} className="text-neutral-300 mx-auto mb-2" />
                <p className="text-sm text-neutral-600">No products found</p>
                <p className="text-xs text-neutral-500 mt-1">Try adjusting your search query</p>
              </div>
            ) : (
              <div className="text-center py-12">
                <Icon name="Package" size={32} className="text-neutral-300 mx-auto mb-2" />
                <p className="text-sm text-neutral-600">Start typing to search</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Step 2: Variant Selection */
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-neutral-50 rounded-lg p-4">
            <div>
              <p className="text-sm text-neutral-600">Selected Product</p>
              <p className="font-medium text-neutral-900">
                {products?.find((p) => p.id === selectedProductId)?.name}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setSelectedProductId(null);
                setSelectedVariants(new Set());
                setSelectedVariantDetails(new Map());
              }}
            >
              <Icon name="ArrowLeft" size={14} className="mr-1" />
              Change
            </Button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-neutral-700">
                Select Variants ({selectedVariants.size} selected)
              </label>
              {variants && variants.length > 0 && (
                <Button type="button" variant="secondary" size="sm" onClick={handleSelectAll}>
                  {selectedVariants.size === variants.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </div>

            <div className="border border-neutral-200 rounded-lg max-h-96 overflow-y-auto">
              {variantsLoading ? (
                <div className="text-center py-12">
                  <Icon
                    name="LoaderCircle"
                    size={32}
                    className="animate-spin text-neutral-400 mx-auto mb-2"
                  />
                  <p className="text-sm text-neutral-600">Loading variants...</p>
                </div>
              ) : variants && variants.length > 0 ? (
                <div className="divide-y divide-neutral-200">
                  {variants.map((variant) => {
                    const isSelected = selectedVariants.has(variant.id);
                    const product = products?.find((p) => p.id === selectedProductId);
                    return (
                      <label
                        key={variant.id}
                        className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${isSelected ? 'bg-primary-50' : 'hover:bg-neutral-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            handleVariantToggle(
                              variant.id,
                              variant.brand_name || '',
                              product?.name ?? ''
                            )
                          }
                          className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-neutral-900">{variant.brand_name}</p>
                          <p className="text-sm text-neutral-600">
                            SKU: {variant.sku || 'N/A'} | HSN: {variant.hsn_code}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Package" size={32} className="text-neutral-300 mx-auto mb-2" />
                  <p className="text-sm text-neutral-600">No variants found</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    This product has no variants. Please create variants first.
                  </p>
                </div>
              )}
            </div>
          </div>

          {selectedVariants.size > 0 && (
            <div className="bg-info-50 border border-info-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} className="text-info-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-info-900">
                  <p className="font-medium">Ready to set prices</p>
                  <p className="text-info-700 mt-1">
                    You&apos;ve selected {selectedVariants.size} variant
                    {selectedVariants.size > 1 ? 's' : ''}. The same price will be applied to all
                    selected variants.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={selectedVariants.size === 0}
        >
          Continue with {selectedVariants.size} variant{selectedVariants.size > 1 ? 's' : ''}
          <Icon name="ArrowRight" size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}
